import { store } from '@restore/store';
import { getPeer, resetPeer, isDetachedHost } from './peer';
import { registerFormHandlers } from './form/handlers';
import { registerFormListeners } from './form/listeners';
import { registerViewerHandlers } from './viewer/handlers';
import { registerViewerListeners } from './viewer/listeners';
import {
  registerMainDetachHandlers,
  registerDetachedHandlers,
} from './detach/handlers';
import {
  registerMainDetachListeners,
  registerDetachedListeners,
} from './detach/listeners';
import { configureRpcRouting } from './helpers';
import { actions, tabCoordinationSelector } from '@restore/readerViewerSession/tabCoordination/tabCoordination.slice';

let disposed: (() => void) | null = null;

const selectTab = () => tabCoordinationSelector(store.getState());

const registerMainRpc = (): (() => void) => {
  registerFormHandlers();
  registerViewerHandlers();

  const unsubscribeNotify = registerFormListeners();

  const unsubscribeHandlers = registerMainDetachHandlers();

  const unsubscribeViewer = registerViewerListeners(action =>
    store.dispatch(action)
  );

  const unsubscribeDetach = registerMainDetachListeners(action =>
    store.dispatch(action)
  );

  return () => {
    unsubscribeViewer();
    unsubscribeDetach();
    unsubscribeHandlers();
    unsubscribeNotify();
  };
};

const registerDetachedRpc = (): (() => void) => {
  const unsubscribeNotify = registerFormListeners();

  const unsubscribeHandlers = registerDetachedHandlers();

  const unsubscribeDetach = registerDetachedListeners(action =>
    store.dispatch(action)
  );

  return () => {
    unsubscribeDetach();
    unsubscribeHandlers();
    unsubscribeNotify();
  };
};

/** Registers only the current host role; repeated initialization is a no-op. */
const startRpc = (): void => {
  if (disposed) return;

  configureRpcRouting(selectTab);
  getPeer();

  const unregisterRpc = isDetachedHost()
    ? registerDetachedRpc()
    : registerMainRpc();

  disposed = () => {
    unregisterRpc();
    resetPeer();
    disposed = null;
  };
};

const extractMainWindowName = (): string | null => {
  const url = new URL(window.location.href);
  const mainWindowName = url.searchParams.get('mainWindowName');

  if (mainWindowName) {
    url.searchParams.delete('mainWindowName');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }

  return mainWindowName;
};

export const initRpc = (): void => {
  if (disposed) return;

  if (isDetachedHost()) {
    const mainWindowName = extractMainWindowName() || window.name;

    if (!mainWindowName) {
      store.dispatch(
        actions.setInitializationError(
          'Detached window has no session SID. Close it and detach again from the main window.'
        )
      );

      store.dispatch(actions.setIsDeactivated(true));

      return;
    }

    window.name = mainWindowName;

    store.dispatch(actions.setInitializationError(null));
  }

  startRpc();
};

/** Tears down host RPC on window departure or explicit teardown. */
export const disposeRpc = (): void => {
  disposed?.();
};
